import { Tooltip } from '@chakra-ui/react';
import LinkIcon from '@mui/icons-material/Link';
import StarIcon from '@mui/icons-material/Star';
import StaffMemberProfessionalExperienceLinksForm from '@/components/StaffMemberProfessionalExperienceLinksForm';
import TextEditor from '@/components/TextEditor';
import { ProjectType } from '@/services/candidateProfile';
import { Profile } from '@/services/candidates';
import { TechStackType } from '@/types';
import { PersonType } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import { onScrollToSmoothly } from '@/utils/global';
import * as Styles from './StaffMemberProfessionalBackground.styles';
import SectionBox from '../../components/SectionBox';

type Props = {
  candidateProfile?: Profile;
  projects?: ProjectType[];
  staffMember?: StaffMember;
  onSubmit?: (data: Partial<Pick<PersonType, 'githubUrl' | 'trioProfileUrl'>>) => void;
};

const StaffMemberProfessionalBackground = ({
  candidateProfile,
  projects = [],
  staffMember,
  onSubmit,
}: Props): JSX.Element => {
  return (
    <Styles.PageGrid>
      <Styles.Sidebar
        sidebarMenus={[
          {
            label: 'Links',
            id: 'sidebar-links',
            onClick: () => onScrollToSmoothly('links'),
          },
          {
            label: 'Expertise',
            id: 'sidebar-expertise',
            onClick: () => onScrollToSmoothly('expertise'),
          },
          {
            label: 'Professional Experience',
            id: 'sidebar-professional-experience',
            onClick: () => onScrollToSmoothly('professional-experience'),
          },
          {
            label: 'Education',
            id: 'sidebar-education',
            onClick: () => onScrollToSmoothly('education'),
          },
          {
            label: 'Projects',
            id: 'sidebar-projects',
            onClick: () => onScrollToSmoothly('projects'),
          },
        ]}
      />
      <Styles.Content>
        <SectionBox title="Links" id="links" className="!tw-pb-[4px]">
          <StaffMemberProfessionalExperienceLinksForm
            githubUrl={staffMember?.person.githubUrl || undefined}
            trioProfileUrl={staffMember?.person.trioProfileUrl || undefined}
            onSubmit={onSubmit}
          />
        </SectionBox>
        <SectionBox title="Expertise" id="expertise">
          {(candidateProfile?.expertises || []).map((expertise) => (
            <div
              className="tw-flex tw-flex-col tw-gap-2 tw-py-6 tw-border-b tw-border-neutral-30 first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0"
              key={expertise.id}
            >
              <div className="tw-flex tw-items-center tw-text-lg tw-leading-tight tw-text-neutral-100 tw-gap-3">
                <p className="tw-font-semibold">{expertise.skill.name}</p>
                <p>{expertise.experience}</p>
                {expertise.isMainStack && (
                  <Tooltip label="Is main stack" placement="right">
                    <StarIcon className="tw-text-primary-60 !tw-w-5 !tw-h-5" />
                  </Tooltip>
                )}
              </div>
              {expertise.subSkills.length > 0 && (
                <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                  {expertise.subSkills.map((subSkill) => (
                    <p
                      className="tw-py-[3px] tw-px-1.5 tw-bg-neutral-30 tw-rounded tw-text-xs tw-leading-tight"
                      key={subSkill.id}
                    >
                      {subSkill.skill.name}
                    </p>
                  ))}
                </div>
              )}
              {expertise.description && (
                <p className="tw-text-sm tw-leading-normal">{expertise.description}</p>
              )}
            </div>
          ))}
        </SectionBox>
        <SectionBox title="Professional Experience" id="professional-experience">
          {(candidateProfile?.workExperiences || []).map((workExperience) => (
            <div
              className="tw-flex tw-flex-col tw-gap-2 tw-py-6 tw-border-b tw-border-neutral-30 first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0"
              key={workExperience.id}
            >
              <div className="tw-flex tw-items-center tw-text-lg tw-leading-tight tw-text-neutral-100 tw-gap-3">
                <p className="tw-font-semibold">{workExperience.company}</p>
                <p>{workExperience.title}</p>
              </div>
              <p className="tw-text-sm tw-leading-normal">{workExperience.location}</p>
              {workExperience.techs.length > 0 && (
                <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                  {workExperience.techs.map((tech: string | TechStackType) => (
                    <p
                      className="tw-py-[3px] tw-px-1.5 tw-bg-neutral-30 tw-rounded tw-text-xs tw-leading-tight"
                      key={typeof tech === 'string' ? tech : tech.id}
                    >
                      {typeof tech === 'string' ? tech : tech.name}
                    </p>
                  ))}
                </div>
              )}
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-leading-normal">
                <p>
                  <span className="tw-text-neutral-80">
                    {workExperience.isCurrentWork ? 'Since' : 'From'}
                  </span>{' '}
                  {workExperience.startMonth}, {workExperience.startYear}
                  {!workExperience.isCurrentWork && (
                    <>
                      {' '}
                      <span className="tw-text-neutral-80">to</span> {workExperience.endMonth},{' '}
                      {workExperience.endYear}
                    </>
                  )}
                </p>
                {workExperience.isCurrentWork && (
                  <p className="tw-font-semibold">Currently work here</p>
                )}
              </div>
              {workExperience.description && (
                <TextEditor
                  markdownClassNames="!tw-text-sm !tw-leading-normal"
                  value={workExperience.description}
                  hideEditButton
                />
              )}
            </div>
          ))}
        </SectionBox>
        <SectionBox title="Education" id="education">
          {(candidateProfile?.educations || []).map((education) => (
            <div
              className="tw-flex tw-flex-col tw-gap-2 tw-py-6 tw-border-b tw-border-neutral-30 first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0"
              key={education.id}
            >
              <div className="tw-flex tw-items-center tw-text-lg tw-leading-tight tw-text-neutral-100 tw-gap-3">
                <p className="tw-font-semibold">{education.university}</p>
                <p>{education.degree}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-leading-normal">
                <p>
                  <span className="tw-text-neutral-80">
                    {education.currentlyPursuing ? 'Since' : 'From'}
                  </span>{' '}
                  {education.startMonth}, {education.startYear}
                  {!education.currentlyPursuing && (
                    <>
                      {' '}
                      <span className="tw-text-neutral-80">to</span> {education.endMonth},{' '}
                      {education.endYear}
                    </>
                  )}
                </p>
                {education.currentlyPursuing && (
                  <p className="tw-font-semibold">Currently pursuing</p>
                )}
              </div>
            </div>
          ))}
        </SectionBox>
        <SectionBox title="Projects" id="projects">
          {projects.map((project) => (
            <div
              className="tw-flex tw-flex-col tw-gap-2 tw-py-6 tw-border-b tw-border-neutral-30 first:tw-pt-0 last:tw-border-b-0 last:tw-pb-0"
              key={project.id}
            >
              <div className="tw-flex tw-items-center tw-text-lg tw-leading-tight tw-text-neutral-100 tw-gap-3">
                <p className="tw-font-semibold">{project.title}</p>
                {project.link && (
                  <a
                    className="tw-text-primary-60 tw-leading-normal tw-text-sm"
                    href={project.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkIcon className="!tw-w-3 !tw-h-3 tw-mr-1.5 !tw-fill-primary-60" />
                    {project.link}
                  </a>
                )}
              </div>
              {project.year && (
                <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-leading-normal">
                  {project.year}
                </p>
              )}
              {project.skills.length > 0 && (
                <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                  {project.skills.map((skill) => (
                    <p
                      className="tw-py-[3px] tw-px-1.5 tw-bg-neutral-30 tw-rounded tw-text-xs tw-leading-tight"
                      key={skill.id}
                    >
                      {skill.skill.name}
                    </p>
                  ))}
                </div>
              )}
              {project.description && (
                <TextEditor
                  markdownClassNames="!tw-text-sm !tw-leading-normal"
                  value={project.description}
                  hideEditButton
                />
              )}
            </div>
          ))}
        </SectionBox>
      </Styles.Content>
    </Styles.PageGrid>
  );
};

export default StaffMemberProfessionalBackground;
